import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import Header from "./Header";
import { useTheme } from "../context/ThemeContext";
import { Loader, ArrowLeft, Calendar, FileText, Zap } from "lucide-react";
import "./Admin.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

export default function UserActionDetails() {
  const {
    isAuthenticated,
    isAdmin,
    loading: authLoading,
    verifyToken,
  } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { userId } = useParams();
  const [actions, setActions] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [userTokensByModel, setUserTokensByModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshingAuth, setRefreshingAuth] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const fetchUserActions = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");
      const headers = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const response = await fetch(
        `${API_URL}/auth/admin/users/${userId}/actions`,
        {
          headers,
          credentials: "include",
        },
      );

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error("Access denied. Admin privileges required.");
        }
        throw new Error("Failed to fetch user actions");
      }

      const data = await response.json();
      setActions(data.actions || []);
      setUserInfo({
        userId: data.userId,
        userName: data.userName,
        userEmail: data.userEmail,
        isAnonymous: data.isAnonymous,
      });
      // Store user's tokensByModel if available (for cases where actions don't have token data)
      if (data.userTokensByModel) {
        setUserTokensByModel(data.userTokensByModel);
      }
    } catch (err) {
      console.error("Error fetching user actions:", err);
      setError(err.message || "Failed to load user actions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    // If user is authenticated but not admin, try refreshing auth once
    if (!isAdmin && !refreshingAuth) {
      setRefreshingAuth(true);
      verifyToken().catch(console.error);
      return;
    }

    // After refresh, if still not admin, show error message
    if (!isAdmin) {
      return;
    }

    fetchUserActions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading, navigate, userId]);

  // Separate effect to handle admin check after refresh
  useEffect(() => {
    if (refreshingAuth && !authLoading) {
      setRefreshingAuth(false);
    }
  }, [authLoading, refreshingAuth]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "0";
    return num.toLocaleString("en-US");
  };

  // Ensure total always matches breakdown: prefer input + output when we have either
  const getTokensFromAction = (action) => {
    const input =
      action?.inputTokens ??
      action?.tokens_input ??
      action?.metadata?.inputTokens ??
      action?.metadata?.tokens_input ??
      0;
    const output =
      action?.outputTokens ??
      action?.tokens_output ??
      action?.metadata?.outputTokens ??
      action?.metadata?.tokens_output ??
      0;
    const cacheCreation =
      action?.cacheCreationTokens ??
      action?.metadata?.cacheCreationTokens ??
      0;
    const cacheRead =
      action?.cacheReadTokens ??
      action?.metadata?.cacheReadTokens ??
      0;
    const sum = input + cacheCreation + cacheRead + output;
    const totalStored =
      action?.totalTokens ??
      action?.tokens_total ??
      action?.metadata?.totalTokens ??
      action?.metadata?.tokens_total ??
      0;
    const totalTokens = sum > 0 ? sum : totalStored;
    return {
      inputTokens: input,
      outputTokens: output,
      cacheCreationTokens: cacheCreation,
      cacheReadTokens: cacheRead,
      totalTokens: totalTokens,
    };
  };

  const getActionTypeLabel = (actionType, metadata) => {
    const labels = {
      initial_query: "Initial Query",
      comparative_analysis: "Comparative Analysis",
      synthesis: "Synthesis",
      debate: "Debate",
      comparative_dashboard: "Comparative Dashboard",
    };
    let label = labels[actionType] || actionType;
    if (actionType === "debate" && metadata?.debateRun != null) {
      label = `Debate Run-${metadata.debateRun}`;
    }
    return label;
  };

  const formatActionType = (actionType, metadata) => {
    const labels = {
      initial_query: "Initial Query",
      comparative_analysis: "Comparative Analysis",
      synthesis: "Synthesis",
      debate: "Debate",
      comparative_dashboard: "Comparative Dashboard",
    };
    let base =
      labels[actionType] ||
      actionType
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    if (actionType === "debate" && metadata?.debateRun != null) {
      base = `Debate Run-${metadata.debateRun}`;
    }
    return base;
  };

  // Group actions by operationId (preferred) or timestamp and action type to combine related actions
  const groupActions = (actions) => {
    const grouped = {};

    actions.forEach((action) => {
      // Use operationId if available (groups actions from same button press)
      // Otherwise fall back to timestamp-based grouping for backward compatibility
      let key;
      if (action.operationId) {
        key = `${action.actionType}_${action.operationId}`;
      } else {
        // Fallback: Create a key based on timestamp (rounded to nearest 3 seconds) and action type
        const timestamp = action.timestamp
          ? new Date(action.timestamp).getTime()
          : 0;
        const timestampKey = Math.floor(timestamp / 3000) * 3000; // Round to nearest 3 seconds
        key = `${action.actionType}_${timestampKey}`;
      }

      if (!grouped[key]) {
        grouped[key] = {
          actionId: action.actionId,
          actionType: action.actionType,
          timestamp: action.timestamp,
          initialQuery: action.initialQuery,
          models: {},
          grandTotal: 0,
          chatId: action.chatId,
          metadata: action.metadata,
        };
      }

      // Skip combined actions if we're processing initial_query (we'll use individual model actions)
      if (
        action.metadata?.isCombined &&
        action.actionType === "initial_query"
      ) {
        // Don't process combined actions for initial_query - we'll calculate from individual models
        return;
      }

      // Parse model names from the model field (could be comma-separated for debate)
      // Normalize model names to match expected format (ChatGPT, Gemini, Claude, Perplexity, Grok)
      const normalizeModelName = (modelName) => {
        if (!modelName) return null;
        const normalized = modelName.trim();
        // Handle variations of model names
        if (
          normalized.toLowerCase().includes("chatgpt") ||
          normalized.toLowerCase().includes("gpt") ||
          normalized.toLowerCase().includes("openai")
        ) {
          return "ChatGPT";
        }
        if (
          normalized.toLowerCase().includes("gemini") ||
          normalized.toLowerCase().includes("google")
        ) {
          return "Gemini";
        }
        if (
          normalized.toLowerCase().includes("claude") ||
          normalized.toLowerCase().includes("anthropic")
        ) {
          return "Claude";
        }
        if (normalized.toLowerCase().includes("perplexity")) {
          return "Perplexity";
        }
        if (
          normalized.toLowerCase().includes("grok") ||
          normalized.toLowerCase().includes("xai")
        ) {
          return "Grok";
        }
        // If it already matches one of our expected names, return as is
        const expectedModels = [
          "ChatGPT",
          "Gemini",
          "Claude",
          "Perplexity",
          "Grok",
        ];
        if (expectedModels.includes(normalized)) {
          return normalized;
        }
        return normalized; // Return original if no match
      };

      const modelNames = action.model
        ? action.model
            .split(",")
            .map((m) => normalizeModelName(m))
            .filter((m) => m)
        : [];

      // For each model mentioned, store its tokens
      modelNames.forEach((modelName) => {
        if (!grouped[key].models[modelName]) {
          grouped[key].models[modelName] = {
            inputTokens: 0,
            outputTokens: 0,
            cacheCreationTokens: 0,
            cacheReadTokens: 0,
            totalTokens: 0,
          };
        }

        // Add tokens for this model (single source of truth for correct count)
        const { inputTokens, outputTokens, cacheCreationTokens, cacheReadTokens, totalTokens } =
          getTokensFromAction(action);
        grouped[key].models[modelName].inputTokens += inputTokens;
        grouped[key].models[modelName].outputTokens += outputTokens;
        grouped[key].models[modelName].cacheCreationTokens += cacheCreationTokens;
        grouped[key].models[modelName].cacheReadTokens += cacheReadTokens;
        grouped[key].models[modelName].totalTokens += totalTokens;
      });
    });

    // Calculate grand total for each grouped action
    Object.keys(grouped).forEach((key) => {
      const action = grouped[key];
      action.grandTotal = Object.values(action.models).reduce((sum, model) => {
        return sum + (model.totalTokens || 0);
      }, 0);
    });

    return Object.values(grouped).sort((a, b) => {
      // Sort by timestamp descending (most recent first)
      return new Date(b.timestamp) - new Date(a.timestamp);
    });
  };

  // Always return all 5 models in a consistent order
  const getAllModels = () => {
    return ["ChatGPT", "Gemini", "Claude", "Perplexity", "Grok"];
  };

  // Group actions once and use for both totals and pagination
  const groupedActions = useMemo(() => {
    const grouped = groupActions(actions);
    // Debug: Log ChatGPT actions with tokens
    if (actions.length > 0) {
      const chatgptActions = actions.filter((a) => {
        const model = a.model ? a.model.toLowerCase() : "";
        return (
          model.includes("chatgpt") ||
          model.includes("gpt") ||
          model.includes("openai")
        );
      });
      const chatgptWithTokens = chatgptActions.filter(
        (a) => (a.totalTokens || a.inputTokens || a.outputTokens || 0) > 0,
      );
      if (chatgptWithTokens.length > 0) {
        console.log(
          `ChatGPT actions with tokens: ${chatgptWithTokens.length} out of ${chatgptActions.length}`,
        );
        console.log(
          "Sample ChatGPT action WITH tokens:",
          JSON.stringify(chatgptWithTokens[0], null, 2),
        );
      } else {
        console.log(
          `No ChatGPT actions with tokens found. All ${chatgptActions.length} have 0 tokens.`,
        );
      }
    }
    return grouped;
  }, [actions]);

  // Calculate token totals from all actions - also check raw actions for tokens
  const tokenTotals = useMemo(() => {
    const allModels = getAllModels();

    const totals = {
      grandTotal: { input: 0, output: 0, cacheCreation: 0, cacheRead: 0, total: 0 },
      byModel: {},
    };

    // Initialize model totals
    allModels.forEach((model) => {
      totals.byModel[model] = { input: 0, output: 0, cacheCreation: 0, cacheRead: 0, total: 0 };
    });

    // First, calculate from raw actions to catch any that might be missed in grouping
    actions.forEach((action) => {
      const modelName = action.model ? action.model.trim() : null;
      if (!modelName) return;

      // Normalize model name
      let normalizedModel = modelName;
      if (
        modelName.toLowerCase().includes("chatgpt") ||
        modelName.toLowerCase().includes("gpt") ||
        modelName.toLowerCase().includes("openai")
      ) {
        normalizedModel = "ChatGPT";
      } else if (
        modelName.toLowerCase().includes("gemini") ||
        modelName.toLowerCase().includes("google")
      ) {
        normalizedModel = "Gemini";
      } else if (
        modelName.toLowerCase().includes("claude") ||
        modelName.toLowerCase().includes("anthropic")
      ) {
        normalizedModel = "Claude";
      } else if (modelName.toLowerCase().includes("perplexity")) {
        normalizedModel = "Perplexity";
      } else if (
        modelName.toLowerCase().includes("grok") ||
        modelName.toLowerCase().includes("xai")
      ) {
        normalizedModel = "Grok";
      }

      if (totals.byModel[normalizedModel]) {
        const { inputTokens, outputTokens, cacheCreationTokens, cacheReadTokens, totalTokens } =
          getTokensFromAction(action);
        totals.byModel[normalizedModel].input += inputTokens;
        totals.byModel[normalizedModel].output += outputTokens;
        totals.byModel[normalizedModel].cacheCreation += cacheCreationTokens;
        totals.byModel[normalizedModel].cacheRead += cacheReadTokens;
        totals.byModel[normalizedModel].total += totalTokens;
      }
    });

    // Calculate grand total
    allModels.forEach((model) => {
      totals.grandTotal.input += totals.byModel[model].input;
      totals.grandTotal.output += totals.byModel[model].output;
      totals.grandTotal.cacheCreation += totals.byModel[model].cacheCreation;
      totals.grandTotal.cacheRead += totals.byModel[model].cacheRead;
      totals.grandTotal.total += totals.byModel[model].total;
    });

    return totals;
  }, [actions]);

  // Paginate grouped actions
  const paginatedActions = useMemo(() => {
    const startIndex = pagination.pageIndex * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return groupedActions.slice(startIndex, endIndex);
  }, [groupedActions, pagination]);

  if (authLoading || refreshingAuth) {
    return (
      <div className={`admin-container ${darkMode ? "dark" : ""}`}>
        <Header />
        <div className="admin-content">
          <div className="admin-loading">
            <Loader className="spin" size={32} />
            <p>Verifying admin access...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!isAdmin) {
    return (
      <div className={`admin-container ${darkMode ? "dark" : ""}`}>
        <Header />
        <div className="admin-content">
          <div className="admin-error">
            <h2>Access Denied</h2>
            <p>
              You do not have admin privileges. If you were recently granted
              admin access, please log out and log back in.
            </p>
            <button
              onClick={() => {
                verifyToken();
                setTimeout(() => {
                  if (!isAdmin) {
                    alert(
                      "Admin access not found. Please log out and log back in after admin status has been granted.",
                    );
                  }
                }, 1000);
              }}
              className="admin-refresh-btn"
              style={{ marginTop: "1rem" }}
            >
              Refresh Admin Status
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-container ${darkMode ? "dark" : ""}`}>
      <Header />
      <div className="admin-content">
        <div className="admin-header">
          <div className="admin-header-title">
            <button
              onClick={() => navigate("/admin")}
              style={{
                background: "none",
                border: "none",
                color: darkMode ? "#fff" : "#333",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginRight: "16px",
                fontSize: "16px",
              }}
            >
              <ArrowLeft size={20} />
              Back to Admin
            </button>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flex: 1,
              }}
            >
              <FileText className="admin-icon" size={32} />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "20px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <h1>User Action Details</h1>
                      {userInfo?.isAnonymous && (
                        <span
                          style={{
                            fontSize: "12px",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            background: darkMode
                              ? "rgba(156, 163, 175, 0.2)"
                              : "#f3f4f6",
                            color: darkMode ? "#9ca3af" : "#4b5563",
                            border: `1px solid ${darkMode ? "rgba(156, 163, 175, 0.3)" : "#e5e7eb"}`,
                          }}
                        >
                          Anonymous Session
                        </span>
                      )}
                    </div>
                    {userInfo && (
                      <p
                        style={{
                          margin: "4px 0 0 0",
                          fontSize: "14px",
                          opacity: 0.8,
                        }}
                      >
                        {userInfo.userName ||
                          (userInfo.isAnonymous ? "Guest User" : "")}{" "}
                        {userInfo.userEmail ? `(${userInfo.userEmail})` : ""}
                      </p>
                    )}
                  </div>
                  {/* Token Summary */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      padding: "12px 16px",
                      background: darkMode
                        ? "rgba(37, 99, 235, 0.1)"
                        : "rgba(37, 99, 235, 0.05)",
                      border: `1px solid ${darkMode ? "rgba(37, 99, 235, 0.3)" : "rgba(37, 99, 235, 0.2)"}`,
                      borderRadius: "8px",
                      minWidth: "300px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: darkMode ? "#60a5fa" : "#2563eb",
                        marginBottom: "4px",
                      }}
                    >
                      Token Usage Summary
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: darkMode ? "#cbd5e1" : "#374151",
                        lineHeight: "1.4",
                      }}
                    >
                      <div>
                        <strong>Grand Total:</strong>{" "}
                        {formatNumber(tokenTotals.grandTotal.total)}
                        <span
                          style={{
                            marginLeft: "8px",
                            color: darkMode ? "#a0a0a0" : "#666",
                          }}
                        >
                          (In: {formatNumber(tokenTotals.grandTotal.input)} |
                          Out: {formatNumber(tokenTotals.grandTotal.output)}
                          {tokenTotals.grandTotal.cacheRead > 0 && ` | Cache Hit: ${formatNumber(tokenTotals.grandTotal.cacheRead)}`}
                          {tokenTotals.grandTotal.cacheCreation > 0 && ` | Cache Write: ${formatNumber(tokenTotals.grandTotal.cacheCreation)}`})
                        </span>
                      </div>
                      <div
                        style={{
                          marginTop: "6px",
                          fontSize: "10px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                        }}
                      >
                        {getAllModels().map((model) => {
                          const modelTotal = tokenTotals.byModel[model];
                          const hasBreakdown =
                            modelTotal.input > 0 || modelTotal.output > 0 || modelTotal.cacheRead > 0 || modelTotal.cacheCreation > 0;
                          return (
                            <div
                              key={model}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <span>
                                <strong>{model}:</strong>
                              </span>
                              {modelTotal.total === 0 ? (
                                <span
                                  style={{ color: darkMode ? "#666" : "#999" }}
                                >
                                  0
                                </span>
                              ) : (
                                <span>
                                  {formatNumber(modelTotal.total)}
                                  {hasBreakdown ? (
                                    <span
                                      style={{
                                        marginLeft: "4px",
                                        color: darkMode ? "#888" : "#999",
                                      }}
                                    >
                                      (In: {formatNumber(modelTotal.input)} |
                                      Out: {formatNumber(modelTotal.output)}
                                      {modelTotal.cacheRead > 0 && ` | Cache Hit: ${formatNumber(modelTotal.cacheRead)}`}
                                      {modelTotal.cacheCreation > 0 && ` | Cache Write: ${formatNumber(modelTotal.cacheCreation)}`})
                                    </span>
                                  ) : (
                                    <span
                                      style={{
                                        marginLeft: "4px",
                                        color: darkMode ? "#888" : "#999",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      (breakdown unavailable)
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={fetchUserActions}
            className="admin-refresh-btn"
            disabled={loading}
          >
            {loading ? <Loader className="spin" size={18} /> : "Refresh"}
          </button>
        </div>

        {error && (
          <div className="admin-error">
            <p>{error}</p>
          </div>
        )}

        {loading ? (
          <div className="admin-loading">
            <Loader className="spin" size={32} />
            <p>Loading user actions...</p>
          </div>
        ) : (
          <div className="admin-table-container">
            {actions.length === 0 ? (
              <div
                className="admin-empty"
                style={{ padding: "40px", textAlign: "center" }}
              >
                <p>No actions found for this user.</p>
              </div>
            ) : (
              (() => {
                const allModels = getAllModels();
                const totalPages = Math.ceil(
                  groupedActions.length / pagination.pageSize,
                );

                return (
                  <>
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Action Performed</th>
                          <th>Timestamp</th>
                          <th>Models Used</th>
                          {allModels.map((model) => (
                            <th key={model}>{model} Tokens</th>
                          ))}
                          <th>Grand Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedActions.map((action, index) => {
                          const modelNames = Object.keys(action.models).filter(
                            (m) => action.models[m].totalTokens > 0,
                          );
                          const modelsUsedText =
                            modelNames.length > 0
                              ? modelNames.join(", ")
                              : action.actionType === "comparative_dashboard"
                                ? "—"
                                : action.metadata?.modelsUsed?.join(", ") ||
                                  action.metadata?.selectedModels?.join(", ") ||
                                  "N/A";
                          const isNoTokenAction =
                            action.actionType === "comparative_dashboard";

                          return (
                            <tr key={action.actionId || index}>
                              <td>
                                <div className="admin-action-type">
                                  <Zap
                                    size={14}
                                    style={{ marginRight: "6px" }}
                                  />
                                  {formatActionType(
                                    action.actionType,
                                    action.metadata,
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="admin-date">
                                  <Calendar size={14} />
                                  {formatDate(action.timestamp)}
                                </div>
                              </td>
                              <td>
                                <div
                                  className="admin-model-badge"
                                  style={{
                                    maxWidth: "250px",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {modelsUsedText}
                                </div>
                              </td>
                              {allModels.map((model) => {
                                const modelData = action.models[model];
                                if (!modelData || modelData.totalTokens === 0) {
                                  return (
                                    <td
                                      key={model}
                                      style={{
                                        color: darkMode ? "#555" : "#ccc",
                                      }}
                                    >
                                      -
                                    </td>
                                  );
                                }
                                return (
                                  <td key={model}>
                                    <div className="admin-token-detail">
                                      <div
                                        style={{
                                          fontSize: "0.6875rem",
                                          color: darkMode ? "#a0a0a0" : "#666",
                                          lineHeight: "1.2",
                                        }}
                                      >
                                        In:{" "}
                                        {formatNumber(modelData.inputTokens)} |
                                        Out:{" "}
                                        {formatNumber(modelData.outputTokens)}
                                      </div>
                                      {(modelData.cacheReadTokens > 0 || modelData.cacheCreationTokens > 0) && (
                                        <div
                                          style={{
                                            fontSize: "0.625rem",
                                            color: darkMode ? "#60a5fa" : "#2563eb",
                                            lineHeight: "1.2",
                                            marginTop: "2px"
                                          }}
                                        >
                                          Cache: {formatNumber(modelData.cacheReadTokens)} hit | {formatNumber(modelData.cacheCreationTokens)} write
                                        </div>
                                      )}
                                      <div
                                        style={{
                                          fontWeight: "600",
                                          fontSize: "0.8125rem",
                                          lineHeight: "1.3",
                                        }}
                                      >
                                        Total:{" "}
                                        {formatNumber(modelData.totalTokens)}
                                      </div>
                                    </div>
                                  </td>
                                );
                              })}
                              <td>
                                <div
                                  className="admin-token-value"
                                  style={{
                                    fontWeight: "700",
                                    fontSize: "0.875rem",
                                    color: darkMode ? "#60a5fa" : "#2563eb",
                                  }}
                                >
                                  {isNoTokenAction
                                    ? "—"
                                    : formatNumber(action.grandTotal)}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginTop: "1rem",
                          padding: "0.75rem",
                          background: darkMode
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.02)",
                          borderRadius: "0.5rem",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "0.875rem",
                            color: darkMode ? "#cbd5e1" : "#374151",
                          }}
                        >
                          Showing{" "}
                          {pagination.pageIndex * pagination.pageSize + 1} to{" "}
                          {Math.min(
                            (pagination.pageIndex + 1) * pagination.pageSize,
                            groupedActions.length,
                          )}{" "}
                          of {groupedActions.length} actions
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <button
                            onClick={() =>
                              setPagination((prev) => ({
                                ...prev,
                                pageIndex: 0,
                              }))
                            }
                            disabled={pagination.pageIndex === 0}
                            style={{
                              padding: "0.375rem 0.75rem",
                              fontSize: "0.875rem",
                              fontWeight: "600",
                              border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                              borderRadius: "0.375rem",
                              background: darkMode ? "#2a2a2a" : "#fff",
                              color: darkMode ? "#fff" : "#333",
                              cursor:
                                pagination.pageIndex === 0
                                  ? "not-allowed"
                                  : "pointer",
                              opacity: pagination.pageIndex === 0 ? 0.5 : 1,
                            }}
                          >
                            {"<<"}
                          </button>
                          <button
                            onClick={() =>
                              setPagination((prev) => ({
                                ...prev,
                                pageIndex: Math.max(0, prev.pageIndex - 1),
                              }))
                            }
                            disabled={pagination.pageIndex === 0}
                            style={{
                              padding: "0.375rem 0.75rem",
                              fontSize: "0.875rem",
                              fontWeight: "600",
                              border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                              borderRadius: "0.375rem",
                              background: darkMode ? "#2a2a2a" : "#fff",
                              color: darkMode ? "#fff" : "#333",
                              cursor:
                                pagination.pageIndex === 0
                                  ? "not-allowed"
                                  : "pointer",
                              opacity: pagination.pageIndex === 0 ? 0.5 : 1,
                            }}
                          >
                            {"<"}
                          </button>
                          <span
                            style={{
                              fontSize: "0.875rem",
                              color: darkMode ? "#cbd5e1" : "#374151",
                              padding: "0 0.5rem",
                            }}
                          >
                            Page {pagination.pageIndex + 1} of {totalPages}
                          </span>
                          <button
                            onClick={() =>
                              setPagination((prev) => ({
                                ...prev,
                                pageIndex: Math.min(
                                  totalPages - 1,
                                  prev.pageIndex + 1,
                                ),
                              }))
                            }
                            disabled={pagination.pageIndex >= totalPages - 1}
                            style={{
                              padding: "0.375rem 0.75rem",
                              fontSize: "0.875rem",
                              fontWeight: "600",
                              border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                              borderRadius: "0.375rem",
                              background: darkMode ? "#2a2a2a" : "#fff",
                              color: darkMode ? "#fff" : "#333",
                              cursor:
                                pagination.pageIndex >= totalPages - 1
                                  ? "not-allowed"
                                  : "pointer",
                              opacity:
                                pagination.pageIndex >= totalPages - 1
                                  ? 0.5
                                  : 1,
                            }}
                          >
                            {">"}
                          </button>
                          <button
                            onClick={() =>
                              setPagination((prev) => ({
                                ...prev,
                                pageIndex: totalPages - 1,
                              }))
                            }
                            disabled={pagination.pageIndex >= totalPages - 1}
                            style={{
                              padding: "0.375rem 0.75rem",
                              fontSize: "0.875rem",
                              fontWeight: "600",
                              border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                              borderRadius: "0.375rem",
                              background: darkMode ? "#2a2a2a" : "#fff",
                              color: darkMode ? "#fff" : "#333",
                              cursor:
                                pagination.pageIndex >= totalPages - 1
                                  ? "not-allowed"
                                  : "pointer",
                              opacity:
                                pagination.pageIndex >= totalPages - 1
                                  ? 0.5
                                  : 1,
                            }}
                          >
                            {">>"}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()
            )}
          </div>
        )}
      </div>
    </div>
  );
}
